/**
 * The session renews itself (docs/refactor/28): a token lives 12 hours, and an app left open at home — or a tab left
 * open at work — would otherwise be signed out every 12 hours. A little before it expires, the app asks the server for
 * a new one with the same proof of identity; a month after the person last signed in, the server says no and the next
 * screen asks for the password, as it should.
 */

import { useEffect } from 'react'
import { api } from './api'
import { getSession, saveSession } from './auth'

/** Renew when less than this is left. */
export const RENEW_BEFORE_MS = 3 * 60 * 60 * 1000
/** How often to look. */
const CHECK_EVERY_MS = 15 * 60 * 1000

/** When the token expires, by its own `exp`; null when it cannot be read. */
export function expiresAt(token: string): number | null {
  try {
    const payload = token.split('.')[1]
    if (!payload) return null
    const exp = (JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as { exp?: number }).exp
    return typeof exp === 'number' ? exp * 1000 : null
  } catch {
    return null
  }
}

/** Whether a token should be renewed now. */
export function dueForRenewal(token: string, now = Date.now()): boolean {
  const expiry = expiresAt(token)
  return expiry !== null && expiry > now && expiry - now < RENEW_BEFORE_MS
}

let renewing: Promise<void> | null = null

/** Renews the stored session if it is due; quietly does nothing otherwise, or when the server refuses. */
export function renewIfDue(): Promise<void> {
  const session = getSession()
  if (!session || !dueForRenewal(session.token)) return Promise.resolve()
  renewing ??= api.auth
    .refresh()
    .then((result) => saveSession(result))
    .catch(() => {
      // Refused (a month since the last sign-in) or offline: the token runs out and the app asks to sign in.
    })
    .finally(() => {
      renewing = null
    })
  return renewing
}

/** Mounted once in the signed-in app: checks on a timer and whenever the window comes back. */
export function useSessionRenewal(signedIn: boolean) {
  useEffect(() => {
    if (!signedIn) return
    void renewIfDue()
    const timer = window.setInterval(() => void renewIfDue(), CHECK_EVERY_MS)
    const back = () => {
      if (document.visibilityState === 'visible') void renewIfDue()
    }
    document.addEventListener('visibilitychange', back)
    window.addEventListener('online', back)
    return () => {
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', back)
      window.removeEventListener('online', back)
    }
  }, [signedIn])
}
